import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import Modal from '../../common/Modal';
import SearchField from '../../common/searchField';
import DataTable from '../../components/DataTable';

import { connectivityDashboardActions } from '../../store/reducers/connectivity-dashboard';
import { NotificationsRequestPayloadTypes } from '../../store/models/connectivity-dashboard';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';

import { ReactComponent as AlertIcon } from '../../assets/alert-icon.svg';
import { ReactComponent as AlertRedIcon } from '../../assets/alert-red-icon.svg';
import { ReactComponent as BellIcon } from '../../assets/icons/main/bell-icon.svg';

const Styles = {
  container: { boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.02)', borderRadius: '18px' },
  paper: {
    margin: 'auto',
    boxShadow: '0px 0px 14px 0px #0000000D',
    borderRadius: '18px',
    border: `1px solid #D9DFED`,
    height: '80vh',
    overflow: 'hidden',
  },
  header: {
    p: 3,
    display: 'flex',
    justifyContent: 'flex-end',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    // height: '87px',
  },
  searchFieldWrapper: { display: 'flex', alignItems: 'center', columnGap: 3 },
  dataTableWrapper: {
    height: '100%',
    overflow: 'hidden',
  },
  dataTable: {
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto',
      minHeight: 'calc(100vh - 560px)',
    },
    '&.MuiDataGrid-root': {
      height: 'calc(80vh - 196px)',
      minHeight: 'calc(80vh - 196px)',
    },
  },
  closeBtnWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBlock: 4,
    borderTop: '1px solid #D9DFED',
  },
  tableCellText: {
    fontSize: '16px',
    fontWeight: 400,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
};

const getNotificationTypeIcon = (type: string) => {
  switch (type) {
    case 'critical':
      return <AlertRedIcon />;
    case 'warning':
      return <AlertIcon />;
    default:
      return <BellIcon />;
  }
};

const getChipColor = (severity: string) => {
  if (severity === 'critical') return 'error';
  if (severity === 'major') return 'warning';
  if (severity === 'low') return 'success';
  return 'warning';
};

const getSeverityChip = (severity: string) => {
  return (
    <Chip
      label={severity}
      color={getChipColor(severity)}
      variant='outlined'
      sx={{
        '& .MuiChip-label': {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    />
  );
};

const NotificationsTable = ({
  onModalClose,
  selectedOntology,
  notifications,
  notificationsLoading,
  notificationsPageNumber,
  notificationsTotalCount,
}: any) => {
  const dispatch = useDispatch();
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const [searchValue, setSearchValue] = useState('');

  const dispatchNotificationsRequest = (payload: NotificationsRequestPayloadTypes) => {
    dispatch(
      connectivityDashboardActions.getNotificationsRequest({
        ...sortingModel,
        onto_manage_id: selectedOntology,
        search_item: searchValue,
        ...payload,
      })
    );
  };

  useEffect(() => {
    dispatchNotificationsRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER });
  }, []);

  const onSearch = (value: string) => {
    dispatchNotificationsRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER, search_item: value });
    setSearchValue(value);
  };

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Type',
      headerAlign: 'center',
      align: 'center',
      width: 120,
      renderCell: (params) => {
        return getNotificationTypeIcon(params.row.type);
      },
    },
    {
      field: 'originator',
      headerName: 'Originator',
      headerAlign: 'left',
      align: 'left',
      width: 180,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'last_connected',
      headerName: 'Date',
      headerAlign: 'left',
      align: 'left',
      width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'severity',
      headerName: 'Severity',
      headerAlign: 'left',
      align: 'left',
      width: 150,
      renderCell: (params) => {
        return getSeverityChip(params.row.severity);
      },
    },
  ];

  return (
    <Modal
      title='LIST OF NOTIFICATIONS'
      open
      customStyles={{ width: '60%', maxWidth: '60%' }}
      handleClose={onModalClose}
    >
      <Container maxWidth={false} disableGutters sx={Styles.container}>
        <Paper sx={Styles.paper}>
          <Box sx={Styles.header}>
            <Box sx={Styles.searchFieldWrapper}>
              <SearchField searchPlaceholder='Search' onSearch={onSearch} width='100%' />
            </Box>
          </Box>
          <Box sx={Styles.dataTableWrapper}>
            <DataTable
              columns={columns}
              rows={notifications}
              loading={notificationsLoading}
              tableStyles={Styles.dataTable}
              isServerPagination
              totalRowCount={notificationsTotalCount}
              pageNumber={notificationsPageNumber}
              onPagination={(pageSize: number, pageNumber: number) => {
                dispatchNotificationsRequest({
                  limit: pageSize,
                  page: pageNumber,
                });
              }}
              onSorting={(sortModel: { field: string; sort: string }) => {
                dispatchNotificationsRequest({
                  limit: DEFAULT_PAGE_SIZE,
                  page: notificationsPageNumber,
                  ...sortModel,
                });
                setSortingModel(sortModel);
              }}
            />
            <Box sx={Styles.closeBtnWrapper}>
              <Button variant='outlined' onClick={onModalClose}>
                Close
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  notifications: state.connectivityDashboard.notifications,
  notificationsLoading: state.connectivityDashboard.notificationsLoading,
  notificationsPageNumber: state.connectivityDashboard.notificationsPageNumber,
  notificationsTotalCount: state.connectivityDashboard.notificationsTotalCount,
});

export default connect(mapStateToProps)(NotificationsTable);
